import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';

import {
    TableRawHTML,
    getFormattedNumber,
} from '@ratehub/base-ui';


function AmortizationTable({
    amortizationSchedule,
    termInYears,
    ...otherProps
}) {
    const intl = useIntl();

    return (
        <TableRawHTML
            {...otherProps}
        >
            {getTableContent(amortizationSchedule, termInYears, intl)}
        </TableRawHTML>
    );
}

AmortizationTable.propTypes = {
    amortizationSchedule: PropTypes.array.isRequired,
    termInYears: PropTypes.number, // In years
};

AmortizationTable.defaultProps = {
    termInYears: 25,
};

// eslint-disable-next-line react/no-multi-comp
function getTableContent(amortizationSchedule, term, intl) {
    // pre-compute term total values
    let principalTotal = 0;
    let interestTotal = 0;
    let paymentTotal = 0;

    amortizationSchedule.forEach((series, index) => {
        if (index < term) {
            principalTotal += series.principal;
            interestTotal += series.interest;
            paymentTotal += series.payment;
        }
    });

    return (
        // TODO: Tech debt to allow TableRawHTML to wrap content within its own <table>
        // Need to determine best solution
        <table className="alignfull">
            <tbody>
                <tr>
                    <th>
                        <FormattedMessage
                            id="mtg-common.chart-amortization-rate.table-heading-year"
                            defaultMessage="Year"
                        />
                    </th>
                    <th>
                        <FormattedMessage
                            id="mtg-common.chart-amortization-rate.table-heading-total-paid"
                            defaultMessage="Total paid"
                        />
                    </th>
                    <th>
                        <FormattedMessage
                            id="mtg-common.chart-amortization-rate.table-heading-principle-paid"
                            defaultMessage="Principal paid"
                        />
                    </th>
                    <th>
                        <FormattedMessage
                            id="mtg-common.chart-amortization-rate.table-heading-interest-paid"
                            defaultMessage="Interest paid"
                        />
                    </th>
                    <th>
                        <FormattedMessage
                            id="mtg-common.chart-amortization-rate.table-heading-balance"
                            defaultMessage="Balance"
                        />
                    </th>
                </tr>

                <For
                    each="scheduleRow"
                    of={amortizationSchedule}
                    index="index"
                >
                    {/* Initial term payments */}
                    <If condition={index < term || scheduleRow.length < term}>
                        <tr>
                            <td className="has-text-align-center">
                                {dayjs().add(index, 'year').format('YYYY')}
                            </td>
                            <td className="has-text-align-center">
                                {getFormattedNumber(scheduleRow.payment, 'CAD0', intl)}
                            </td>
                            <td className="has-text-align-center">
                                {getFormattedNumber(scheduleRow.principal, 'CAD0', intl)}
                            </td>
                            <td className="has-text-align-center">
                                {getFormattedNumber(scheduleRow.interest, 'CAD0', intl)}
                            </td>
                            <td className="has-text-align-center">
                                {getFormattedNumber(scheduleRow.balance, 'CAD0', intl)}
                            </td>
                        </tr>
                    </If>

                    {/* Term totals */}
                    <If condition={index === term}>
                        <tr className="term-totals">
                            <td className="has-text-align-center rh-title-3xs">
                                <FormattedMessage
                                    id="mtg-common.chart-amortization-rate.table-term-total-label"
                                    defaultMessage="Total (term)"
                                />
                            </td>
                            <td className="has-text-align-center rh-title-3xs">
                                {getFormattedNumber(paymentTotal, 'CAD0', intl)}
                            </td>
                            <td className="has-text-align-center rh-title-3xs">
                                {getFormattedNumber(principalTotal, 'CAD0', intl)}
                            </td>
                            <td className="has-text-align-center rh-title-3xs">
                                {getFormattedNumber(interestTotal, 'CAD0', intl)}
                            </td>
                            <td className="has-text-align-center rh-title-3xs">
                                {getFormattedNumber(amortizationSchedule[index-1]?.balance, 'CAD0', intl)}
                            </td>
                        </tr>

                        <tr>
                            <td
                                colSpan="5"
                                className="padded"
                            >
                                <p className="rh-text-m">
                                    <FormattedMessage
                                        id="mtg-common.chart-amortization-rate.table-term-description"
                                        defaultMessage="The line above displays the totals at the end of your mortgage
                                            term. At this time, you will renew your mortgage and choose among the rates
                                            that are available. The following analysis assumes you will lock in the same
                                            rate for the remainder of the amortization period, which may not be possible."
                                    />
                                </p>
                            </td>
                        </tr>
                    </If>

                    {/* Remaining amortization */}
                    <If condition={index >= term}>
                        <tr>
                            <td className="has-text-align-center">
                                {dayjs().add(index, 'year').format('YYYY')}
                            </td>
                            <td className="has-text-align-center">
                                {getFormattedNumber(scheduleRow.payment, 'CAD0', intl)}
                            </td>
                            <td className="has-text-align-center">
                                {getFormattedNumber(scheduleRow.principal, 'CAD0', intl)}
                            </td>
                            <td className="has-text-align-center">
                                {getFormattedNumber(scheduleRow.interest, 'CAD0', intl)}
                            </td>
                            <td className="has-text-align-center">
                                {getFormattedNumber(scheduleRow.balance, 'CAD0', intl)}
                            </td>
                        </tr>
                    </If>

                    {/* Full amortization totals */}
                    <If condition={index === amortizationSchedule.length - 1}>
                        <tr className="grand-totals">
                            <td className="has-text-align-center rh-title-3xs">
                                <FormattedMessage
                                    id="mtg-common.chart-amortization-rate.table-term-grand-total-label"
                                    defaultMessage="Total"
                                />
                            </td>
                            <td className="has-text-align-center rh-title-3xs">
                                {getFormattedNumber(getGrandTotals(amortizationSchedule, 'payment'), 'CAD0', intl)}
                            </td>
                            <td className="has-text-align-center rh-title-3xs">
                                {getFormattedNumber(getGrandTotals(amortizationSchedule, 'principal'), 'CAD0', intl)}
                            </td>
                            <td className="has-text-align-center rh-title-3xs">
                                {getFormattedNumber(getGrandTotals(amortizationSchedule, 'interest'), 'CAD0', intl)}
                            </td>
                            <td className="has-text-align-center rh-title-3xs">
                                {getFormattedNumber(scheduleRow.balance, 'CAD0', intl)}
                            </td>
                        </tr>
                    </If>
                </For>
            </tbody>
        </table>
    );
}

function getGrandTotals(schedule, key) {
    return schedule.reduce((total, current) => total + current[key], 0);
}

export default AmortizationTable;
